import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizMultipleChoice, {
  type QuizMultipleChoiceState,
} from "@/components/storyboard/quiz/QuizMultipleChoice";
import { useQuizValidations } from "@/hooks/useQuizValidations";
import { InteractiveElementStatusKind } from "@/lib/interactiveElementState";
import type { QuizMultipleChoiceEntryData } from "@/types/storyboard";
import type { InteractiveIllustrationKind } from "@/lib/interactiveIllustration";

export interface QuizMultipleChoiceFlowProps {
  id: string;
  title: string;
  entries: QuizMultipleChoiceEntryData[];
  illustrationKind?: InteractiveIllustrationKind;
  /**
   * "code" stacks the snippet under the prompt in the inline card
   * (CODE_MULTIPLE_CHOICE), matching CODE_TRUE_FALSE.
   */
  variant?: "default" | "code";
  onValidate: (
    selectedLabels: string[],
    entryIndex: number
  ) => Promise<{ correct: boolean | null; feedback?: string }>;
}

/** Multiple-choice quiz in the shared interactive-element shell — inline in the
 * storyboard card (including CODE_MULTIPLE_CHOICE). The verdicts live on the
 * server, so a half-finished quiz reopens at the first unanswered question.
 */
const QuizMultipleChoiceFlow = ({
  id,
  title,
  entries,
  illustrationKind = "multiple_choice",
  variant = "default",
  onValidate,
}: QuizMultipleChoiceFlowProps) => {
  const {
    status: statusOverride,
    isReady: validationsReady,
    refresh,
  } = useQuizValidations(
    id,
    entries.length,
    (answeredCount, correctCount): QuizMultipleChoiceState => ({
      currentIndex: answeredCount,
      selectedLabels: [],
      isSubmitted: false,
      isCorrect: null,
      correctCount,
    })
  );
  const serverResume =
    statusOverride?.kind === InteractiveElementStatusKind.InProgress
      ? (statusOverride.partial as QuizMultipleChoiceState)
      : undefined;

  return (
    <QuizInteractiveFlow<QuizMultipleChoiceState>
      id={id}
      title={title}
      illustrationKind={illustrationKind}
      layout="inline"
      statusOverride={statusOverride}
      reportProgress
      renderQuiz={({
        initialPartial,
        initialIndex,
        onPartialChange,
        onComplete,
      }) => {
        // The quiz seeds from useState on first mount: wait for the local
        // in-progress state or for validation_data to settle, so a resume does
        // not start at question 1.
        if (initialPartial == null && !validationsReady) return null;

        return (
          <QuizMultipleChoice
            entries={entries}
            onValidate={async (...args) => {
              const verdict = await onValidate(...args);
              refresh();
              return verdict;
            }}
            variant={variant}
            initialPartial={initialPartial ?? serverResume}
            initialIndex={initialIndex ?? serverResume?.currentIndex}
            onPartialChange={onPartialChange}
            onComplete={onComplete}
          />
        );
      }}
    />
  );
};

export default QuizMultipleChoiceFlow;
